import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, AlertCircle, ExternalLink } from "lucide-react";

export default function VendorDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("products");

  useEffect(() => {
    document.title = "Vendor Dashboard — PFC";
    if (!user) {
      nav(`/login?next=${encodeURIComponent("/vendor/dashboard")}`);
      return;
    }
    refresh();
  }, [user, nav]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [meRes, pRes, oRes] = await Promise.all([
        api.get("/vendor/me"),
        api.get("/vendor/products").catch(() => ({ data: [] })),
        api.get("/vendor/orders").catch(() => ({ data: [] })),
      ]);
      setVendor(meRes.data);
      setProducts(pRes.data || []);
      setOrders(oRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="bg-ink-900 min-h-screen py-24 text-center text-white/40">Loading…</div>;

  const v = vendor?.vendor;

  if (!v) {
    return (
      <div className="bg-ink-900 min-h-screen py-24 text-center" data-testid="no-vendor">
        <p className="text-white/70 mb-6">You haven't registered as a vendor yet.</p>
        <Link to="/vendor/register" className="px-6 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright">
          Register Now →
        </Link>
      </div>
    );
  }

  if (!v.activation_paid || !v.subscription_active) {
    return (
      <div className="bg-ink-900 min-h-screen py-24 text-center px-5" data-testid="vendor-inactive">
        <AlertCircle className="mx-auto text-pfc-gold mb-4" />
        <h2 className="font-display text-2xl text-white mb-3">Storefront not active</h2>
        <p className="text-white/70 mb-6 max-w-md mx-auto">
          {!v.activation_paid
            ? "Complete your $99 activation to unlock product listing."
            : "Your subscription has lapsed. Renew $9.99 / month to re-list your products."}
        </p>
        <Link to="/vendor/register" className="px-6 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright">
          Finish Setup →
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-ink-900 min-h-screen text-white" data-testid="vendor-dashboard">
      <div className="max-w-[1200px] mx-auto px-5 lg:px-10 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-pfc-gold text-[11px] tracking-[0.4em] uppercase font-sub mb-2">Vendor Dashboard</div>
            <h1 className="font-display text-3xl sm:text-4xl">{v.business_name}</h1>
            <Link to={`/marketplace/vendor/${v.slug}`} className="text-white/60 hover:text-pfc-gold text-xs inline-flex items-center gap-1 mt-2">
              View public storefront <ExternalLink size={11} />
            </Link>
          </div>
          <button
            onClick={() => setEditing({ title: "", description: "", price: 0, images: [""], sizes: [], category: "apparel", active: true })}
            data-testid="add-product-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-xs hover:bg-pfc-gold-bright"
          >
            <Plus size={14} /> Add Product
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 border-b border-white/10 mb-6" data-testid="vendor-tabs">
          {[
            { k: "products", label: `Products (${products.length})` },
            { k: "orders", label: `Orders (${orders.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`pb-3 text-sm uppercase tracking-[0.2em] font-sub transition border-b-2 ${
                tab === t.k ? "text-pfc-gold border-pfc-gold" : "text-white/50 border-transparent hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-products-grid">
            {products.length === 0 && <div className="col-span-full text-white/40 py-12 text-center">No products yet. Click "Add Product" to start.</div>}
            {products.map((p) => (
              <div key={p.product_id} className="border border-white/10 bg-ink-800/40 p-4" data-testid={`my-product-${p.product_id}`}>
                <div className="aspect-square bg-white/5 mb-3 overflow-hidden">
                  {(p.images || [])[0] ? (
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-white/30"><Package size={32} /></div>
                  )}
                </div>
                <h3 className="font-sub text-white">{p.title}</h3>
                <div className="text-pfc-gold font-display text-xl mt-1">${p.price.toFixed(2)}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(p)} className="flex-1 px-3 py-2 border border-white/15 text-xs uppercase font-sub tracking-[0.2em] hover:bg-white/5 inline-flex items-center justify-center gap-1" data-testid={`edit-${p.product_id}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Delete this product?")) return;
                      try {
                        await api.delete(`/vendor/products/${p.product_id}`);
                        toast.success("Product deleted.");
                        refresh();
                      } catch (err) {
                        toast.error(formatApiError(err?.response?.data?.detail));
                      }
                    }}
                    className="px-3 py-2 border border-pfc-red/30 text-pfc-red text-xs hover:bg-pfc-red/10"
                    data-testid={`delete-${p.product_id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="orders-list">
            {orders.length === 0 && <div className="text-white/40 py-12 text-center">No orders yet.</div>}
            {orders.map((o) => (
              <div key={o.order_id} className="border border-white/10 p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-center text-sm" data-testid={`order-${o.order_id}`}>
                <div>
                  <div className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-sub">Order</div>
                  <div className="text-white truncate">{o.order_id}</div>
                </div>
                <div>
                  <div className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-sub">Product</div>
                  <div className="text-white">{o.product_title}</div>
                </div>
                <div>
                  <div className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-sub">Buyer</div>
                  <div className="text-white">{o.shipping?.full_name || o.buyer_email}</div>
                </div>
                <div>
                  <div className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-sub">Amount</div>
                  <div className="text-pfc-gold">${o.amount?.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-sub">Status</div>
                  <div className={o.payment_status === "paid" ? "text-emerald-400" : "text-white/60"}>{o.payment_status}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <ProductEditor
            product={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refresh();
            }}
          />
        )}
      </div>
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: product.title || "",
    description: product.description || "",
    price: product.price || 0,
    currency: product.currency || "usd",
    images: product.images?.length ? product.images : [""],
    category: product.category || "apparel",
    sizes: product.sizes || [],
    external_url: product.external_url || "",
    active: product.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        images: form.images.filter((x) => x?.trim()),
        sizes: typeof form.sizes === "string" ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : form.sizes,
      };
      if (product.product_id) {
        await api.put(`/vendor/products/${product.product_id}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/vendor/products", payload);
        toast.success("Product created.");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="product-editor">
      <div className="bg-ink-900 border border-pfc-gold/40 max-w-xl w-full p-8">
        <div className="flex items-baseline justify-between mb-6">
          <h3 className="font-display text-2xl text-white">{product.product_id ? "Edit Product" : "Add Product"}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm" data-testid="close-editor">Close</button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <Input label="Title" required value={form.title} onChange={(v) => update("title", v)} testId="ed-title" />
          <Textarea label="Description" required value={form.description} onChange={(v) => update("description", v)} testId="ed-desc" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Price (USD)" required type="number" step="0.01" value={form.price} onChange={(v) => update("price", v)} testId="ed-price" />
            <Input label="Category" value={form.category} onChange={(v) => update("category", v)} testId="ed-cat" />
          </div>
          <Input label="Image URL" required value={form.images[0] || ""} onChange={(v) => update("images", [v])} testId="ed-image" />
          <Input label="Sizes (comma-separated)" value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes} onChange={(v) => update("sizes", v)} testId="ed-sizes" placeholder="S, M, L, XL" />
          <Input label="External Buy URL (optional)" value={form.external_url} onChange={(v) => update("external_url", v)} testId="ed-ext" placeholder="If set, Buy redirects there instead of PFC checkout" />
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={form.active} onChange={(e) => update("active", e.target.checked)} data-testid="ed-active" />
            Active (visible in storefront)
          </label>
          <button type="submit" disabled={saving} data-testid="save-product-btn" className="w-full py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.25em] text-sm hover:bg-pfc-gold-bright transition disabled:opacity-60">
            {saving ? "Saving…" : "Save Product"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text", placeholder, step, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <input
        type={type}
        step={step}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-pfc-gold focus:outline-none"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, required, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] font-sub text-white/60 mb-1.5">{label}{required && <span className="text-pfc-red"> *</span>}</div>
      <textarea
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        rows={4}
        className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white focus:border-pfc-gold focus:outline-none resize-y"
      />
    </label>
  );
}
